import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Gift, Check, Trophy } from 'lucide-react'
import { usePremios, usePremioActivo } from '../hooks/useSorteos'
import type { Premio } from '../types/sorteos.types'

interface Props {
  sorteoId: string
  selectedPremioId: string | null
  onSelectPremio: (id: string) => void
}

export function PremioActualCard({ sorteoId, selectedPremioId, onSelectPremio }: Props) {
  const { data: activoData } = usePremioActivo(sorteoId)
  const { data: premios = [] } = usePremios(sorteoId)

  if (!activoData?.hay_premios && premios.length === 0) return null

  // Premio mostrado: el seleccionado por el usuario o el activo del backend
  const premioActivo = activoData?.premio
  const premioSeleccionado: Premio | undefined =
    selectedPremioId
      ? premios.find(p => p.id === selectedPremioId)
      : premioActivo ?? undefined

  return (
    <Card className="border border-teal-500/30 dark:border-teal-500/20">
      <CardHeader className="py-3 pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-teal-500" />
            Premio a Sortear
          </span>
          {activoData?.todos_agotados && (
            <Badge className="badge-gradient text-xs">Todos entregados</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {/* Premio seleccionado actualmente */}
        {premioSeleccionado ? (
          <>
            {premioSeleccionado.imagen_url && (
              <img
                src={premioSeleccionado.imagen_url}
                alt={premioSeleccionado.nombre}
                className="max-h-28 rounded-lg object-contain mx-auto"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
            )}
            <div>
              <p className="font-semibold text-base text-black dark:text-white">{premioSeleccionado.nombre}</p>
              {premioSeleccionado.descripcion && (
                <p className="text-xs text-muted-foreground mt-0.5">{premioSeleccionado.descripcion}</p>
              )}
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Entregados</span>
                <span className="font-medium text-black dark:text-white">
                  {premioSeleccionado.ganadores_count} / {premioSeleccionado.cantidad}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-teal-500/10">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    background: 'linear-gradient(90deg, #14b8a6, #0f766e)',
                    width: `${Math.min(100, premioSeleccionado.cantidad > 0 ? (premioSeleccionado.ganadores_count / premioSeleccionado.cantidad) * 100 : 0)}%`,
                  }}
                />
              </div>
              <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">
                {premioSeleccionado.restantes} restante{premioSeleccionado.restantes !== 1 ? 's' : ''}
              </p>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2 py-3 text-muted-foreground">
            <Trophy className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-center">
              {activoData?.todos_agotados
                ? 'Todos los premios han sido entregados'
                : 'No hay premios configurados'}
            </p>
          </div>
        )}

        {/* Lista de premios con selección */}
        {premios.length > 0 && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-1">
            <p className="text-xs text-muted-foreground mb-1.5">Seleccionar premio a sortear:</p>
            {premios.map(p => {
              const isSelected = p.id === (selectedPremioId ?? premioActivo?.id)
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-2 py-1.5 px-2 rounded-md text-sm transition-colors
                    ${isSelected ? 'bg-teal-500/10 border border-teal-500/20' : 'border border-transparent'}
                    ${p.agotado ? 'opacity-50' : ''}`}
                >
                  <span className="flex-1 font-medium text-black dark:text-white truncate">{p.nombre}</span>
                  <span className="text-xs text-muted-foreground shrink-0">{p.ganadores_count}/{p.cantidad}</span>
                  {p.agotado ? (
                    <Check className="h-3.5 w-3.5 text-teal-500 shrink-0" />
                  ) : isSelected ? (
                    <Trophy className="h-3.5 w-3.5 text-teal-500 shrink-0" />
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => onSelectPremio(p.id)}
                      className="h-6 text-[10px] px-2 border-teal-500/50 text-teal-700 dark:text-teal-400 hover:bg-teal-500/10 shrink-0"
                    >
                      Sortear
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
